import { useMemo } from 'react';

export type UseApplicantDataParams = {
  application: any,
  applicantId: string,
  isApplicationLoading: boolean,
};

export type UseApplicantDataReturn = {
  isProspect: boolean,
  isFinanciallyResponsible: boolean,
  applicantTypeName: string | null,
  affordableRelationship: string | null,
};

export default function useApplicantData({
  application,
  applicantId,
  isApplicationLoading,
}: UseApplicantDataParams): UseApplicantDataReturn {
  return useMemo(() => {
    if (!application?.applicants || !applicantId) {
      return {
        isProspect: false,
        isFinanciallyResponsible: false,
        applicantTypeName: null,
        affordableRelationship: null,
      };
    }
    // Find the specific applicant in the application data
    const applicant = application.applicants.find(
      (app) => app.id === applicantId,
    );

    if (!applicant) {
      return {
        isProspect: false,
        isFinanciallyResponsible: false,
        applicantTypeName: null,
        affordableRelationship: null,
      };
    }

    // Determine if this is a prospect (primary applicant)
    const isProspect = applicant.applicantCustomer?.isProspect === true;

    // Determine if this is financially responsible
    // For prospects, they are financially responsible by default
    // For non-prospects, check the applicantType.financiallyResponsible flag
    const isFinanciallyResponsible =
      isProspect ||
      (applicant.applicantCustomer != null &&
        applicant.applicantType?.financiallyResponsible === true);

    // Get applicant type name
    const applicantTypeName = applicant.applicantType?.name || null;

    // Get affordable relationship
    const affordableRelationship =
      applicant.affordableRelationship?.name || null;

    return {
      isProspect,
      isFinanciallyResponsible,
      applicantTypeName,
      affordableRelationship,
    };
  }, [application, applicantId]);
}
